/**
 * `sync.decks.deck(id).cards.*` — card mutations and reactive reads.
 *
 * All mutations are O(1) on the normalized
 * `cards: Record<deckName, Record<cardId, CardProps>>` store. Per-scope
 * timestamps (`sharedUpdatedAtISO`, `personalUpdatedAtISO`) are stamped
 * automatically based on which fields the partial touches — see
 * `stampsForPartial` for the rule.
 */

import { CARD_FIELD_SCOPES } from '@flashka/shared/sync-engine/schema/field-scopes';
import type {
  DeckId,
  SyncCardProps as CardProps,
} from '@flashka/shared/sync-engine/schema/shared-types';

import {
  cleanupNotesMarks,
  clearUndoSnapshot,
  restoreNotesMarks,
} from '../card-notes-cleanup';
import {
  useComposedCard,
  useComposedCards,
  useComposedCardsRecord,
} from '../internal/store/composed-selectors';
import { observeClientCardPartition } from '../internal/telemetry/scope-observer';
import {
  backfillSortKeys,
  isInsertPosition,
  reassignSortKeys,
  sortKeysBetween,
} from '../internal/utils/sort-key';
import { compareCardOrder } from '../internal/utils/sort-key-compare';
import { store } from './helpers';
import type { CardEditable, NewCard, ScopedCards } from './types';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * A mutation addressed a card this deck's bucket doesn't hold. The guards
 * below still no-op (there is nothing safe to write), but they must be LOUD:
 * historically they swallowed real user actions with no trace. With duplicate
 * `deckPaths` (a recovered `[BACKUP]` deck kept the original's paths) the
 * path→id resolvers could hand a WRITE a different twin than the one on
 * screen, and every delete / edit / star / study rating of a perfectly
 * visible card evaporated here — user-reported as "I can't delete this card"
 * and "studying doesn't register". A dropped WRITE is never routine; log it
 * so the misroute is diagnosable from the console/log aggregator.
 */
function warnDroppedCardWrite(op: string, deckId: string, cardId: string): void {
  console.warn(
    `[sync.cards] ${op} dropped: card ${cardId} not in deck ${deckId} — ` +
      'wrong deck resolved for a duplicated deckPath, or the deck is not resident',
  );
}

/** Build a full CardProps from user-provided fields + defaults. */
function buildCard(input: NewCard): CardProps {
  return {
    ...input,
    id: input.id ?? Math.random().toString(),
    createdTimeISO: input.createdTimeISO ?? new Date().toISOString(),
    actions: input.actions ?? [],
    studyState: input.studyState ?? [],
  };
}

/**
 * Get visible (non-deleted) cards in the canonical card order from a card map.
 * Used for calculating insertion positions. `compareCardOrder` is the same
 * comparator mobile's `sortCardsByKey` uses — the id tiebreak matters, see
 * its doc.
 */
function getVisibleSorted(cardMap: Record<string, CardProps>): CardProps[] {
  return Object.values(cardMap)
    .filter((c) => !c.deletedTimeISO)
    .sort(compareCardOrder);
}

/**
 * The highest `sortKey` among visible cards (null when none is visible) —
 * the key of the last card in `getVisibleSorted` order when every card holds
 * a key. Byte-order compare, like `compareSortKeys`.
 */
function maxVisibleSortKey(cards: readonly CardProps[]): string | null {
  let max: string | null = null;
  for (const c of cards) {
    if (c.deletedTimeISO) continue;
    const key = c.sortKey!;
    if (max === null || key > max) max = key;
  }
  return max;
}

/**
 * Decide which timestamps to bump for a given partial update.
 *
 * Option F splits the single `updatedAtISO` into a `sharedUpdatedAtISO`
 * / `personalUpdatedAtISO` pair so cross-scope LWW stays unambiguous
 * (a shared-field broadcast from a linked group doesn't compete with a
 * local personal-field edit that happened to land at the same moment).
 *
 * Rules:
 *   - Any shared-scope key in the partial → bump `sharedUpdatedAtISO`.
 *   - Any personal-scope key in the partial → bump `personalUpdatedAtISO`.
 *   - Always bump the legacy `updatedAtISO` so pre-Option-F readers
 *     still see the write.
 *
 * An empty partial (shouldn't happen in practice) still bumps
 * `updatedAtISO` for parity with the old behaviour.
 */
function stampsForPartial(partial: Partial<CardProps>): {
  updatedAtISO: string;
  sharedUpdatedAtISO?: string;
  personalUpdatedAtISO?: string;
} {
  const now = new Date().toISOString();
  let hasShared = false;
  let hasPersonal = false;
  for (const key of Object.keys(partial) as Array<keyof CardProps>) {
    const scope = CARD_FIELD_SCOPES[key as keyof typeof CARD_FIELD_SCOPES];
    if (scope === 'shared') hasShared = true;
    else if (scope === 'personal') hasPersonal = true;
  }
  return {
    updatedAtISO: now,
    ...(hasShared ? { sharedUpdatedAtISO: now } : {}),
    ...(hasPersonal ? { personalUpdatedAtISO: now } : {}),
  };
}

function isLinkedDeck(deckId: string): boolean {
  return (store().deckProperties[deckId]?.linkedGroups?.length ?? 0) > 0;
}

/** Every key of `partial` already holds that value on `existing`. Field values
 *  are strings or small JSON shapes (images, tags), so JSON equality is enough —
 *  a key-order difference only costs a write, never skips a real one. */
export function editChangesNothing(
  existing: CardProps,
  partial: Record<string, unknown>,
): boolean {
  return Object.entries(partial).every(([key, value]) => {
    const current = (existing as unknown as Record<string, unknown>)[key];
    if (current === value) return true;
    if (typeof current !== 'object' || typeof value !== 'object') return false;
    if (current === null || value === null) return false;
    return JSON.stringify(current) === JSON.stringify(value);
  });
}

/** Set a single card with per-scope timestamps stamped from the partial. */
function setCardStamped(
  deckId: string,
  existing: CardProps,
  partial: Partial<CardProps>,
  source: string = 'card-edit',
): void {
  observeClientCardPartition(partial, {
    source,
    deckId,
    isLinked: isLinkedDeck(deckId),
  });
  store().setCard(deckId, {
    ...existing,
    ...partial,
    ...stampsForPartial(partial),
  });
}

/** Returns a fresh-card stamp — both timestamps set to now. Used when
 *  adding brand-new cards (they're "new" in every scope). */
function freshCardStamps(): {
  updatedAtISO: string;
  sharedUpdatedAtISO: string;
  personalUpdatedAtISO: string;
} {
  const now = new Date().toISOString();
  return {
    updatedAtISO: now,
    sharedUpdatedAtISO: now,
    personalUpdatedAtISO: now,
  };
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createCardsApi(deckId: DeckId): ScopedCards {
  // ── Card reads (Phase 4: composed for linked decks) ──
  //
  // For personal-only decks the composed selectors short-circuit to
  // `useFlashkaStore.cards[deckId]` exactly like the old read path. For
  // linked decks they compose the SG-side shared half with the
  // per-user `linkedCardOverlay`. Lazy: missing overlay → scope
  // defaults (empty study state, no stars, etc.).

  const use = (): CardProps[] => useComposedCards(deckId);
  const useRecord = (): Record<string, CardProps> => useComposedCardsRecord(deckId);
  const useFind = (id: string): CardProps | undefined => useComposedCard(deckId, id);

  // ── Card mutations (O(1) per card) ──

  const add = (card: NewCard, position?: number): void => {
    addMany([card], position);
  };

  const addMany = (newCards: NewCard[], position?: number): void => {
    const built = newCards.map(buildCard);
    const cardMap = store().cards[deckId] ?? {};
    const resident = Object.values(cardMap);
    const filled = backfillSortKeys(resident);

    // Compute sort keys for new cards
    let before: string | null;
    let after: string | null;

    if (!isInsertPosition(position) && filled === resident) {
      // Append, and every resident card already holds a unique key
      // (`backfillSortKeys` returned its input): the insertion point is the
      // highest visible key — one scan instead of building and sorting the
      // visible list. Same key `getVisibleSorted(...).at(-1)` yields.
      before = maxVisibleSortKey(resident);
      after = null;
    } else if (!isInsertPosition(position)) {
      const visible = getVisibleSorted(Object.fromEntries(filled.map((c) => [c.id, c])));
      before = visible.length > 0 ? visible[visible.length - 1].sortKey! : null;
      after = null;
    } else {
      const visible = getVisibleSorted(Object.fromEntries(filled.map((c) => [c.id, c])));
      before = position > 0 ? (visible[position - 1]?.sortKey ?? null) : null;
      after = position < visible.length ? (visible[position]?.sortKey ?? null) : null;
    }

    const keys = sortKeysBetween(before, after, built.length);
    const fresh = freshCardStamps();
    const now = fresh.updatedAtISO;

    // Collect all cards to write atomically (backfilled + new)
    const batch: CardProps[] = [];

    // Backfilled sort keys — `sortKey` is shared scope. Bump shared
    // only; a concurrent personal-field edit that happened at the same
    // instant shouldn't lose its personal timestamp.
    // (None when `backfillSortKeys` returned its input untouched.)
    for (const c of filled === resident ? [] : filled) {
      if (c.sortKey !== cardMap[c.id]?.sortKey) {
        batch.push({
          ...c,
          updatedAtISO: now,
          sharedUpdatedAtISO: now,
        });
      }
    }

    // New cards — fresh in every scope, stamp both timestamps. A card that
    // arrives WITH study history (a move/merge from another deck) into a deck
    // that was already reset keeps it: it joined after the floor.
    const floor = store().deckProperties[deckId]?.progressResetAtISO;
    for (let i = 0; i < built.length; i++) {
      const card = built[i];
      const carriesHistory =
        floor !== undefined &&
        (card.studyState.length > 0 || card.actions.some((a) => !a.delete));
      batch.push({
        ...card,
        sortKey: keys[i],
        ...fresh,
        // max(): a slow clock must not stamp the join before the floor.
        ...(carriesHistory ? { joinedDeckAtISO: now > floor ? now : floor } : {}),
      });
    }

    // Single atomic write — one Zustand set, one subscription fire
    store().setCardsBatch(deckId, batch);
  };

  const edit = (cardId: string, partial: CardEditable): void => {
    const existing = store().cards[deckId]?.[cardId];
    if (!existing) return warnDroppedCardWrite('edit', deckId, cardId);
    // Drop keys whose value is explicitly `undefined`: spreading them in
    // setCardStamped would OVERWRITE the stored field with undefined (wiping
    // e.g. a card's text). `undefined` never means "clear" here — clears use
    // `null`/`''` precisely because `undefined` doesn't survive JSON anyway.
    const cleaned = Object.fromEntries(
      Object.entries(partial).filter(([, v]) => v !== undefined),
    );
    if (Object.keys(cleaned).length === 0) return;
    // An edit that changes nothing is not a write. Saving a card the user
    // opened and closed without typing (every editor exit saves) used to stamp
    // it, re-sync it and repaint every list holding the deck — two whole-deck
    // re-renders per untouched close on mobile. Personal decks only: a linked
    // deck's text lives in the group's half, so the row here can be stale and
    // equal to an edit that DOES change the card.
    if (!isLinkedDeck(deckId) && editChangesNothing(existing, cleaned)) return;
    setCardStamped(deckId, existing, cleaned as Partial<CardProps>);
  };

  const del = (cardId: string): void => {
    const existing = store().cards[deckId]?.[cardId];
    if (!existing) return warnDroppedCardWrite('delete', deckId, cardId);
    const now = new Date().toISOString();
    // `deletedTimeISO` is shared (tombstones propagate); the appended
    // `{delete}` action is personal (per-user history). Both scopes
    // change so both timestamps bump.
    setCardStamped(deckId, existing, {
      deletedTimeISO: now,
      actions: [...existing.actions, { delete: { ISO: now } }],
    } as Partial<CardProps>);
    void cleanupNotesMarks([existing]);
  };

  const deleteMany = (cardIds: string[]): void => {
    const now = new Date().toISOString();
    const cardMap = store().cards[deckId];
    if (!cardMap) {
      for (const id of cardIds) warnDroppedCardWrite('deleteMany', deckId, id);
      return;
    }
    const originals: CardProps[] = [];
    const batch: CardProps[] = [];
    for (const id of cardIds) {
      const existing = cardMap[id];
      if (!existing) {
        warnDroppedCardWrite('deleteMany', deckId, id);
        continue;
      }
      originals.push(existing);
      // `deletedTimeISO` is shared + `{delete}` action is personal →
      // bump both timestamps.
      batch.push({
        ...existing,
        deletedTimeISO: now,
        updatedAtISO: now,
        sharedUpdatedAtISO: now,
        personalUpdatedAtISO: now,
        actions: [...existing.actions, { delete: { ISO: now } }],
      } as CardProps);
    }
    if (batch.length > 0) {
      store().setCardsBatch(deckId, batch);
      void cleanupNotesMarks(originals);
    }
  };

  const restore = (cardId: string): void => {
    const existing = store().cards[deckId]?.[cardId];
    if (!existing) return warnDroppedCardWrite('restore', deckId, cardId);
    // Restore clears the shared tombstone AND the personal delete-action
    // history. Both timestamps bump.
    //
    // `null` (not `undefined`) so the cleared field survives JSON
    // serialisation across the wire — `undefined` keys are dropped by
    // `JSON.stringify`, which would leave the SG-side `deletedTimeISO`
    // tombstone in place (study-group peers never see the restore).
    setCardStamped(deckId, existing, {
      deletedTimeISO: null,
      actions: existing.actions.filter((a) => !a.delete),
    });
    void restoreNotesMarks([cardId]);
  };

  const restoreMany = (cardIds: string[]): void => {
    const cardMap = store().cards[deckId];
    if (!cardMap) {
      for (const id of cardIds) warnDroppedCardWrite('restoreMany', deckId, id);
      return;
    }
    const now = new Date().toISOString();
    const batch: CardProps[] = [];
    for (const id of cardIds) {
      const existing = cardMap[id];
      if (!existing) {
        warnDroppedCardWrite('restoreMany', deckId, id);
        continue;
      }
      batch.push({
        ...existing,
        // `null` (not `undefined`) — see `restore()` above.
        deletedTimeISO: null,
        updatedAtISO: now,
        // Both scopes change on restore — stamp both timestamps so
        // cross-scope LWW doesn't reject either side's subsequent edits.
        sharedUpdatedAtISO: now,
        personalUpdatedAtISO: now,
        actions: existing.actions.filter((a) => !a.delete),
      });
    }
    if (batch.length > 0) {
      store().setCardsBatch(deckId, batch);
      void restoreNotesMarks(cardIds);
    }
  };

  const purge = (cardIds: string | string[]): void => {
    const ids = Array.isArray(cardIds) ? cardIds : [cardIds];
    const cardMap = store().cards[deckId];
    const toPurge: CardProps[] = [];
    for (const id of ids) {
      if (cardMap?.[id]) toPurge.push(cardMap[id]);
      clearUndoSnapshot(id);
    }
    // One store set, one count reconcile, one storage delete for the batch —
    // not one of each per card.
    store().removeCards(deckId, ids);
    if (toPurge.length > 0) void cleanupNotesMarks(toPurge, false);
  };

  const star = (cardId: string, starred: boolean): void => {
    const existing = store().cards[deckId]?.[cardId];
    if (!existing) return warnDroppedCardWrite('star', deckId, cardId);
    // `starred` is personal scope only.
    setCardStamped(deckId, existing, { starred });
  };

  const starMany = (cardIds: string[], starred: boolean): void => {
    const cardMap = store().cards[deckId];
    if (!cardMap) return;
    const now = new Date().toISOString();
    const batch: CardProps[] = [];
    for (const id of cardIds) {
      const existing = cardMap[id];
      if (!existing) continue;
      batch.push({
        ...existing,
        starred,
        updatedAtISO: now,
        // Star is per-user; only the personal timestamp bumps so a
        // shared-field broadcast with the same timestamp doesn't get
        // starved.
        personalUpdatedAtISO: now,
      });
    }
    if (batch.length > 0) store().setCardsBatch(deckId, batch);
  };

  const flipMany = (cardIds?: string[]): void => {
    const cardMap = store().cards[deckId];
    if (!cardMap) return;
    const now = new Date().toISOString();
    const batch: CardProps[] = [];
    const only = cardIds ? new Set(cardIds) : null;
    for (const c of Object.values(cardMap)) {
      if (only && !only.has(c.id)) continue;
      if (c.front?.startsWith('<img src=')) continue;
      // `front` and `back` are shared scope — stamp `sharedUpdatedAtISO`
      // so the partition router pushes the swap to linked study-group
      // peers. Routing through `updateDeckCards` only bumps the legacy
      // `updatedAtISO`, which the SG fan-out ignores.
      batch.push({
        ...c,
        front: c.back || '',
        back: c.front || '',
        updatedAtISO: now,
        sharedUpdatedAtISO: now,
      });
    }
    if (batch.length > 0) store().setCardsBatch(deckId, batch);
  };

  const setImage = (
    cardId: string,
    position: 'front' | 'back',
    image: { src: string; format: { width: string; height: string } },
  ): void => {
    const existing = store().cards[deckId]?.[cardId];
    if (!existing) return warnDroppedCardWrite('setImage', deckId, cardId);
    const emptySlot = { src: '', format: { width: '', height: '' } };
    const images = {
      front: position === 'front' ? image : existing.images?.front || emptySlot,
      back: position === 'back' ? image : existing.images?.back || emptySlot,
    } satisfies CardProps['images'];
    // `images` is shared scope.
    setCardStamped(deckId, existing, { images });
  };

  /**
   * A card's index in the deck's VISIBLE ORDER — the same space `addMany`'s
   * `position` is read in, so `addMany(cards, positionOf(x) + 1)` lands them
   * straight after `x`.
   *
   * It has to walk the SORTED list, not the map: the store's insertion order
   * drifts from sort order the moment anything is inserted rather than
   * appended, and an index from the wrong space silently puts new cards
   * somewhere else in the deck.
   */
  /**
   * The deck's live cards, sorted, outside React.
   *
   * Non-reactive on purpose: the callers are one-shot operations that need to
   * see the deck as it is at the moment they run (detaching a source counts
   * the cards anchored in it), not to re-render when it changes.
   */
  const getVisible = (): CardProps[] => {
    const cardMap = store().cards[deckId];
    return cardMap ? getVisibleSorted(cardMap) : [];
  };

  const positionOf = (cardId: string): number | undefined => {
    const cardMap = store().cards[deckId];
    if (!cardMap) return undefined;
    const index = getVisibleSorted(cardMap).findIndex((card) => card.id === cardId);
    return index === -1 ? undefined : index;
  };

  const reorder = (orderedCardIds: string[]): void => {
    // Multi-card operation — uses the transform bridge
    store().updateDeckCards(deckId, (cards) => {
      const idToCard = new Map(cards.map((c) => [c.id, c]));

      const reordered = orderedCardIds
        .map((id) => idToCard.get(id))
        .filter(Boolean) as CardProps[];
      const reorderedWithKeys = reassignSortKeys(reordered);

      const reorderedIdSet = new Set(orderedCardIds);
      const remaining = cards.filter((c) => !reorderedIdSet.has(c.id));

      return [...reorderedWithKeys, ...remaining];
    });
  };

  const transform = (fn: (cards: CardProps[]) => CardProps[]): void => {
    store().updateDeckCards(deckId, fn);
  };

  return {
    use,
    useRecord,
    useFind,
    add,
    addMany,
    edit,
    delete: del,
    deleteMany,
    getVisible,
    restore,
    restoreMany,
    purge,
    star,
    starMany,
    flipMany,
    setImage,
    positionOf,
    reorder,
    transform,
  };
}
